// react
import React from 'react';
import styled from 'styled-components';
import { css } from 'styled-components';

// components
import { Icons } from '@/components/atoms/Icon/icons';
import { Button } from '@/components/atoms/Button/Button';
import { Icon } from '@/components/atoms/Icon/Icon';

import { LoadingTransition } from '@/components/atoms/LoadingTransition/LoadingTransition';
import { Skeleton } from '@/components/atoms/Skeleton/Skeleton';

interface ServiceCardProps {
  className?: string;
  children?: React.ReactNode;
  title: string;
  description1: string;
  description2?: string;
  icon?: Icons;
  size: 'sm' | 'md';
  isLoading: boolean;
  onBookCall: () => void;
  onLearnMore: () => void;
}

/**
 * ServiceCard is a container for Service content.
 */
export const ServiceCard = ({
  className,
  title,
  description1,
  description2,
  icon,
  size = 'md',
  isLoading = true,
  onBookCall,
  onLearnMore,
}: ServiceCardProps) => {
  return (
    <StyledServiceCard
      className={className}
      $size={size}
      $isLoading={isLoading}
    >
      <StyledIcon>
        <LoadingTransition
          isLoading={isLoading}
          loading={<Skeleton shape='avatar' width='40px' height='40px' />}
          content={icon && <Icon icon={icon} fontSize={40} />}
        />
      </StyledIcon>
      <Body>
        <Gap />
        <Title>
          <LoadingTransition
            isLoading={isLoading}
            loading={<Skeleton width='200px' shape='title' />}
            content={title}
          />
        </Title>
        <LoadingTransition
          isLoading={isLoading}
          loading={<Skeleton shape='subtitle' />}
          content={<Description>{description1}</Description>}
        />

        <HoverContent>
          <Description>{description2}</Description>
        </HoverContent>
      </Body>
      <ButtonWrapper>
        <Button
          variant='secondary'
          size='md'
          ariaLabel='Learn more'
          onPress={onLearnMore}
        >
          Learn more
        </Button>
        <Button
          variant='primary'
          size='md'
          ariaLabel='Get in touch'
          onPress={onBookCall}
        >
          Get in touch
        </Button>
      </ButtonWrapper>
    </StyledServiceCard>
  );
};

interface StyledServiceCardProps {
  $isLoading: boolean;
  $size: 'sm' | 'md';
}
const StyledServiceCard = styled.div<StyledServiceCardProps>`
  position: relative;
  display: flex;
  flex-direction: column;
  background: ${(props) => props.theme.color.neutral[10]};
  border: 1px solid ${(props) => props.theme.color.neutral[60]};
  border-radius: 8px;
  padding: ${(props) => props.theme.spacing[32]};
  box-shadow: ${(props) => props.theme.elevation[2]};
  box-sizing: border-box;
  transition-duration: 0.2s;
  width: 100%;
  overflow: hidden;

  ${(props) => props.$isLoading && 'pointer-events: none'};

  ${(props) => {
    switch (props.$size) {
      case 'sm':
        return css`
          height: 280px;
        `;
      case 'md':
        return css`
          height: 340px;
        `;
    }
  }}

  &:hover {
    box-shadow: ${(props) => props.theme.elevation[5]};
    transform: scale(1.02);

    ${(props) =>
      props.$size === 'sm' &&
      css`
        height: 340px;
      `}
  }
`;

const Gap = styled.div`
  flex: 1;
  transition-duration: 0.2s;
  ${StyledServiceCard}:hover & {
    flex: 0;
  }
`;

const StyledIcon = styled.div`
  color: ${(props) => props.theme.color.neutral[90]};
  margin-bottom: ${(props) => props.theme.spacing[12]};
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;

  transition-duration: 0.2s;
  gap: ${(props) => props.theme.spacing[12]};

  ${StyledServiceCard}:hover & {
    padding-bottom: 110px;
  }
`;

const Title = styled.div`
  font: ${(props) => props.theme.typography.heading['3xl']};
  color: ${(props) => props.theme.color.neutral[100]};
`;

const Description = styled.div`
  font: ${(props) => props.theme.typography.body.md.regular};
  color: ${(props) => props.theme.color.neutral[70]};

  min-height: 0;
`;

const HoverContent = styled.div`
  transition-duration: 0.2s;
  min-height: 0;
  opacity: 0;
  flex: 0;
  transition-duration: 0.2s;
  ${StyledServiceCard}:hover & {
    opacity: 1;
    flex: 1;
  }
`;

const ButtonWrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;

  display: flex;
  gap: ${(props) => props.theme.spacing[16]};

  overflow: hidden;

  padding: ${(props) => props.theme.spacing[32]};
  width: 100%;

  transition-duration: 0.2s;
  opacity: 0;

  pointer-events: none;

  ${StyledServiceCard}:hover & {
    opacity: 1;
    pointer-events: auto;
  }

  & > button {
    flex: 1;
  }
`;

ServiceCard.displayName = 'ServiceCard';

export default ServiceCard;
